import './ProfileHealthCard.css';
import { Button, Card, CardContent, CircularProgress, Skeleton, Typography } from '@mui/material';
import { useAuth } from 'context/AuthContext';
import { useDashboard } from 'context/DashboardContext';
import { useProfile } from 'context/ProfileContext';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardView } from 'utils/enums';


type ScoreTier = 'complete' | 'oneStep' | 'almostComplete' | 'halfway' | 'goodStart' | 'continueSetup' | 'empty';

const getScoreTier = (score: number): ScoreTier => {
    if (score === 100) return 'complete';
    if (score >= 90)   return 'oneStep';
    if (score >= 70)   return 'almostComplete';
    if (score >= 50)   return 'halfway';
    if (score >= 30)   return 'goodStart';
    if (score >= 10)   return 'continueSetup';
    return 'empty';
};

const getProgressColor = (score: number): 'error' | 'warning' | 'success' | 'primary' => {
    if (score < 30) return 'error';
    if (score < 70) return 'warning';
    if (score < 100) return 'success';
    return 'primary';
};

const ProfileHealthCard = () => {
    const { t } = useTranslation();
    const { setView } = useDashboard();
    const { state, loading, hasChanges } = useProfile();
    const { user: { userImage } } = useAuth();

    const criteria = useMemo(() => {
        const { links, info } = state.currentProfile;
        const { description } = info as { description?: string };
        return [
            { key: 'avatar', label: t('dashboard.main.health.criteria.avatar'), done: !!userImage?.fileName, points: 25, criteriaView: DashboardView.PROFILE },
            { key: 'bio', label: t('dashboard.main.health.criteria.bio'), done: !!description, points: 25, criteriaView: DashboardView.PROFILE },
            { key: 'links', label: t('dashboard.main.health.criteria.links'), done: links.length >= 1, points: 30, criteriaView: DashboardView.LINKS },
            { key: 'links3', label: t('dashboard.main.health.criteria.links3'), done: links.length >= 3, points: 20, criteriaView: DashboardView.LINKS },
        ];
    }, [state, userImage, t]);

    const score = useMemo(() =>
        criteria.reduce((acc, c) => acc + (c.done ? c.points : 0), 0),
    [criteria]);

    const tier = useMemo(() => getScoreTier(score), [score]);

    const statusLabel = t(`dashboard.main.health.label.${tier}`);

    const buttonLabel = useMemo(() => {
        if (tier === 'oneStep')        return t('dashboard.main.health.btn.finishProfile');
        if (tier === 'halfway')        return t('dashboard.main.health.btn.finishSetup');
        return t('dashboard.main.health.btn.completeProfile');
    }, [tier, t]);

    const handleAction = () => {
        const nextCriterion = criteria.find(c => !c.done);
        if (nextCriterion) setView(nextCriterion.criteriaView);
    };

    return (
        <Card variant="outlined" className='dashboard-card health'>
            <CardContent className='health-card-content'>
                <Typography variant="body1" component="p" className='health-title'>
                    {t('dashboard.main.health.title')}
                </Typography>
                <div className="health-body">
                    <div className='health-body-text'>
                        {loading ? (
                            <>
                                <Skeleton variant="text" width="55%" height={22} />
                                <div className='health-criteria'>
                                    <Skeleton variant="text" width="35%" height={14} />
                                    <Skeleton variant="text" width="70%" height={18} />
                                    <Skeleton variant="text" width="60%" height={18} />
                                </div>
                            </>
                        ) : (
                            <>
                                <Typography variant="body2" className='health-status-label'>
                                    {statusLabel}
                                </Typography>
                                {score < 100 && (
                                    <div className='health-criteria'>
                                        <span className='health-criteria-label'>
                                            {t('dashboard.main.health.pending')}:
                                        </span>
                                        {criteria.filter(c => !c.done).map(item => (
                                            <div key={item.key} className='health-criterion'>
                                                <span className='health-criterion-dot' />
                                                <span className='health-criterion-label'>{item.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className='health-circle-container'>
                        {loading ? (
                            <Skeleton variant="circular" width={120} height={120} />
                        ) : (
                            <>
                                <CircularProgress
                                    variant="determinate"
                                    value={100}
                                    size={120}
                                    thickness={5.5}
                                    className='health-circle-track'
                                />
                                <CircularProgress
                                    variant="determinate"
                                    value={score}
                                    size={120}
                                    thickness={5.5}
                                    color={getProgressColor(score)}
                                />
                                <Typography variant="body2" className='health-score-text'>
                                    {score}%
                                </Typography>
                            </>
                        )}
                    </div>
                </div>
                {(loading || score < 100) && (
                    <div className='health-footer'>
                        <Button
                            color="primary"
                            variant={!loading && state.isPublished && !hasChanges && score < 100 ? "contained" : "outlined"}
                            size="large"
                            disabled={loading}
                            onClick={handleAction}
                        >
                            {loading ? <div className='btn-text-container'><Skeleton width={120} height={16} /></div> : buttonLabel}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default ProfileHealthCard;
